import * as React from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { useLocalStorage } from '@buffer-mono/popcorn'

import { useQueryParam } from '~publish/hooks/useQueryParam'

import { DEFAULT_STATUS } from './types'
import type { FilterStatus } from './types'

export const COMMENTS_STATUS_FILTER_STORAGE_KEY = 'comments-status-filter'
export const PERSIST_STATUS_FILTER_SPLIT = 'geid-cet-persist-status-filter'

/**
 * Reads and writes the comment status filter with optional cross-session
 * persistence.
 *
 * When the split flag is on, the selected status is stored in localStorage
 * so it survives navigation and new sessions (cleared on cache clear).
 * Priority: URL param (shareable/bookmarkable) > localStorage > hardcoded
 * default. When the flag is off, behaves identically to a plain
 * useQueryParam: status is read from the URL only, falling back to
 * DEFAULT_STATUS.
 *
 * This hook is a pure projection of the URL and localStorage — it runs no
 * effects, so any number of components can call it concurrently. The sync
 * effects that mirror localStorage to the URL on page entry live in
 * `PersistedStatusSync`, mounted once per Community view.
 */
export const usePersistedStatusParam = (): [
  status: FilterStatus,
  setStatus: (status: FilterStatus) => void,
  persistStatus: (status: FilterStatus | undefined) => void,
] => {
  const { isEnabled } = useSplitEnabled(PERSIST_STATUS_FILTER_SPLIT)
  const [statusParam, setStatusParam] = useQueryParam<FilterStatus>('status')

  // Undefined default so we can distinguish "user never set a preference"
  // from "user explicitly chose the default value".
  const [persistedStatus, setPersistedStatus] = useLocalStorage<
    FilterStatus | undefined
  >(COMMENTS_STATUS_FILTER_STORAGE_KEY, undefined)

  const status = isEnabled
    ? (statusParam ?? persistedStatus ?? DEFAULT_STATUS)
    : (statusParam ?? DEFAULT_STATUS)

  // Persist-only write (no URL navigation), for callers that batch the URL
  // update themselves — e.g. setFilters in useFilters. Passing `undefined`
  // clears the stored preference, so a batch that drops the status param
  // doesn't leave the read fallback pointing at a stale value.
  const persistStatus = React.useCallback(
    (newStatus: FilterStatus | undefined): void => {
      if (isEnabled) {
        setPersistedStatus(newStatus)
      }
    },
    [isEnabled, setPersistedStatus],
  )

  const setStatus = React.useCallback(
    (newStatus: FilterStatus): void => {
      persistStatus(newStatus)
      setStatusParam(newStatus)
    },
    [persistStatus, setStatusParam],
  )

  return [status, setStatus, persistStatus]
}
